import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import AppTextInput from "../../components/inputs/AppTextInput";
import AppLoadingOverlay from "../../components/loaders/AppLoadingOverlay";
import { PageHeader } from "../../components/cards/PageHeader";
import { AppColors } from "../../constants/AppColors";
import { useProfileScreen } from "./hooks/useProfileScreen";
import ThemeSettingsCard from "../../components/profile/ThemeSettingsCard";
import { canManageBilling } from "../../utils/helperFunctions/roleHelper";
import { MERCHANT_INFO } from "../../constants/MerchantInfo";
import { isCustomSubscription } from "../../utils/helperFunctions/subscriptionAccessHelper";

const MenuCard = ({
  title,
  description,
  icon,
  open,
  onClick,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  open: boolean;
  onClick: () => void;
}) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        mb: 1.5,
        borderRadius: 3,
        cursor: "pointer",
        transition: "0.2s ease",
        "&:hover": {
          borderColor: AppColors.primary,
          transform: "translateY(-1px)",
        },
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "16px",
              bgcolor: AppColors.primarySoft,
              color: AppColors.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>

          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Stack>
      </CardContent>
    </Card>
  );
};

const SectionCard = ({ children }: { children: React.ReactNode }) => {
  return (
    <Card sx={{ mb: 2, borderRadius: 3 }}>
      <CardContent>{children}</CardContent>
    </Card>
  );
};

const InformationRow = ({
  label,
  value,
  href,
}: {
  label: string;
  value: string;
  href?: string;
}) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
        gap: { xs: 0.5, sm: 2 },
        py: 1.25,
        borderBottom: `1px solid ${AppColors.border}`,
        "&:last-of-type": {
          borderBottom: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          color: AppColors.textPrimary,
        }}
      >
        {label}
      </Typography>

      {href ? (
        <Typography
          component="a"
          href={href}
          target={href.startsWith("http") ? "_blank" : undefined}
          rel={href.startsWith("http") ? "noreferrer" : undefined}
          sx={{
            color: AppColors.primary,
            textDecoration: "none",
            overflowWrap: "anywhere",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {value}
        </Typography>
      ) : (
        <Typography
          sx={{
            color: AppColors.textSecondary,
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};

const ProfileScreen = () => {
  const vm = useProfileScreen();

  const isOwner = canManageBilling(vm.user?.role);
  const showSubscriptionManagement =
    isOwner && !isCustomSubscription(vm.account);
  const showPricing = vm.activeMenuSection === "pricing";
  const showSupport = vm.activeMenuSection === "support";
  const showSettings = vm.activeMenuSection === "settings";
  const showPrivacy = vm.activeMenuSection === "privacy";

  const currentPlan =
    vm.account?.effective_subscription_plan ||
    vm.account?.subscription_plan ||
    "basic";

  return (
    <Box>
      <AppLoadingOverlay visible={vm.loading} />

      <PageHeader title={vm.t("tabs.menu")} />

      {vm.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {vm.error}
        </Alert>
      ) : null}

      {showSubscriptionManagement ? (
        <>
          <MenuCard
            title={vm.t("menu.pricing")}
            description={vm.t("menu.pricingDescription")}
            icon={<CardMembershipOutlinedIcon />}
            open={showPricing}
            onClick={() =>
              vm.setActiveMenuSection(showPricing ? null : "pricing")
            }
          />

          <Collapse in={showPricing} unmountOnExit>
            <SectionCard>
              <Typography variant="h6" sx={{ fontWeight: 950, mb: 1 }}>
                {vm.t("menu.currentPlan")}
              </Typography>

              <Typography
                sx={{
                  display: "inline-flex",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  bgcolor: AppColors.primarySoft,
                  color: AppColors.primary,
                  fontWeight: 950,
                  textTransform: "capitalize",
                  mb: 1,
                }}
              >
                {currentPlan}
              </Typography>

              <Typography color="text.secondary">
                {vm.t("menu.pricingComingSoon")}
              </Typography>
            </SectionCard>
          </Collapse>
        </>
      ) : null}

      <MenuCard
        title={vm.t("menu.myAccount")}
        description={vm.t("menu.myAccountDescription")}
        icon={<AccountCircleOutlinedIcon />}
        open={vm.isAccountOpen}
        onClick={() => vm.setAccountOpen(!vm.isAccountOpen)}
      />

      <Collapse in={vm.isAccountOpen} unmountOnExit>
        <SectionCard>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 2 }}>
            {vm.t("profile.title")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            <AppTextInput
              label={vm.t("profile.firstName")}
              placeholder={vm.t("profile.firstNamePlaceholder")}
              value={vm.firstName}
              onChange={(e) => vm.setFirstName(e.target.value)}
            />

            <AppTextInput
              label={vm.t("profile.lastName")}
              placeholder={vm.t("profile.lastNamePlaceholder")}
              value={vm.lastName}
              onChange={(e) => vm.setLastName(e.target.value)}
            />

            <AppTextInput
              label={vm.t("profile.phone")}
              placeholder={vm.t("profile.phonePlaceholder")}
              value={vm.phone}
              onChange={(e) => vm.setPhone(e.target.value)}
            />
          </Box>

          <Button
            sx={{ mt: 2 }}
            variant="contained"
            onClick={vm.handleSaveProfile}
            disabled={vm.loading}
          >
            {vm.t("profile.saveProfile")}
          </Button>
        </SectionCard>

        <SectionCard>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 2 }}>
            {vm.t("profile.updateEmail")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr auto" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <AppTextInput
              label={vm.t("profile.email")}
              placeholder={vm.t("profile.emailPlaceholder")}
              value={vm.email}
              onChange={(e) => vm.setEmail(e.target.value)}
            />

            <AppTextInput
              type="password"
              label={vm.t("profile.currentPassword")}
              placeholder={vm.t("profile.currentPasswordPlaceholder")}
              value={vm.emailCurrentPassword}
              onChange={(e) => vm.setEmailCurrentPassword(e.target.value)}
            />

            <Button
              variant="contained"
              onClick={vm.handleSaveEmail}
              disabled={vm.loading}
            >
              {vm.t("profile.updateEmail")}
            </Button>
          </Box>
        </SectionCard>

        <SectionCard>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 2 }}>
            {vm.t("profile.updatePassword")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr auto" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <AppTextInput
              type="password"
              label={vm.t("profile.currentPassword")}
              placeholder={vm.t("profile.currentPasswordPlaceholder")}
              value={vm.currentPassword}
              onChange={(e) => vm.setCurrentPassword(e.target.value)}
            />

            <AppTextInput
              type="password"
              label={vm.t("profile.newPassword")}
              placeholder={vm.t("profile.newPasswordPlaceholder")}
              value={vm.newPassword}
              onChange={(e) => vm.setNewPassword(e.target.value)}
            />

            <AppTextInput
              type="password"
              label={vm.t("profile.confirmNewPassword")}
              placeholder={vm.t("profile.confirmNewPasswordPlaceholder")}
              value={vm.newPasswordConfirmation}
              onChange={(e) => vm.setNewPasswordConfirmation(e.target.value)}
            />

            <Button
              variant="contained"
              onClick={vm.handleSavePassword}
              disabled={vm.loading}
            >
              {vm.t("profile.updatePassword")}
            </Button>
          </Box>
        </SectionCard>

        {isOwner ? (
          <SectionCard>
            <Typography variant="h6" sx={{ fontWeight: 950, mb: 1 }}>
              {vm.t("accountDeletion.title")}
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {vm.account?.deletion_requested
                ? vm.t("accountDeletion.alreadyScheduled", {
                    date: vm.account.deletion_scheduled_at || "-",
                  })
                : vm.t("accountDeletion.description")}
            </Typography>

            {vm.account?.deletion_requested ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {vm.t("accountDeletion.cancelInfo")}
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                {vm.t("accountDeletion.warning")}
              </Alert>
            )}

            {vm.account?.deletion_requested ? (
              <Button
                variant="outlined"
                color="warning"
                onClick={vm.handleCancelAccountDeletion}
                disabled={vm.loading}
              >
                {vm.t("accountDeletion.cancelDeletion")}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="error"
                onClick={vm.handleRequestAccountDeletion}
                disabled={vm.loading}
              >
                {vm.t("accountDeletion.requestDeletion")}
              </Button>
            )}
          </SectionCard>
        ) : null}
      </Collapse>

      <MenuCard
        title={vm.t("menu.settings")}
        description={vm.t("menu.settingsDescription")}
        icon={<SettingsOutlinedIcon />}
        open={showSettings}
        onClick={() =>
          vm.setActiveMenuSection(showSettings ? null : "settings")
        }
      />

      <Collapse in={showSettings} unmountOnExit>
        {isOwner ? (
          <SectionCard>
            <Typography variant="h6" sx={{ fontWeight: 950, mb: 2 }}>
              {vm.t("profile.currencySettings")}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                gap: 2,
                alignItems: "center",
              }}
            >
              <AppTextInput
                select
                label={vm.t("currency.defaultCurrency")}
                value={vm.defaultCurrency}
                onChange={(e) => vm.setDefaultCurrency(e.target.value)}
              >
                <MenuItem value="BAM">BAM / KM</MenuItem>
                <MenuItem value="EUR">EUR / €</MenuItem>
                <MenuItem value="USD">USD / $</MenuItem>
                <MenuItem value="RSD">RSD</MenuItem>
              </AppTextInput>

              <Button
                variant="contained"
                onClick={vm.handleSaveCurrency}
                disabled={vm.loading}
                sx={{ height: 54 }}
              >
                {vm.t("common.save")}
              </Button>
            </Box>
          </SectionCard>
        ) : null}

        <ThemeSettingsCard t={vm.t} />

        <SectionCard>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 2 }}>
            {vm.t("profile.language")}
          </Typography>

          <AppTextInput
            select
            label={vm.t("profile.language")}
            value={vm.language}
            onChange={(e) => vm.handleLanguageChange(e.target.value as any)}
          >
            <MenuItem value="bs">Bosanski</MenuItem>
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="de">Deutsch</MenuItem>
            <MenuItem value="fr">Français</MenuItem>
            <MenuItem value="es">Español</MenuItem>
            <MenuItem value="hr">Hrvatski</MenuItem>
            <MenuItem value="sr">Српски</MenuItem>
          </AppTextInput>
        </SectionCard>
      </Collapse>

      <MenuCard
        title={vm.t("menu.privacy")}
        description={vm.t("menu.privacyDescription")}
        icon={<PrivacyTipOutlinedIcon />}
        open={showPrivacy}
        onClick={() => vm.setActiveMenuSection(showPrivacy ? null : "privacy")}
      />

      <Collapse in={showPrivacy} unmountOnExit>
        <SectionCard>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 1 }}>
            {vm.t("merchant.title")}
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {vm.t("merchant.description")}
          </Typography>

          <InformationRow
            label={vm.t("merchant.legalName")}
            value={MERCHANT_INFO.legalName}
          />

          <InformationRow
            label={vm.t("merchant.address")}
            value={MERCHANT_INFO.address}
          />

          <InformationRow
            label={vm.t("merchant.businessActivity")}
            value={MERCHANT_INFO.businessActivity}
          />

          <InformationRow
            label={vm.t("merchant.activityCode")}
            value={MERCHANT_INFO.activityCode}
          />

          <InformationRow
            label={vm.t("merchant.registrationNumber")}
            value={MERCHANT_INFO.registrationNumber}
          />

          {/*<InformationRow
            label={vm.t("merchant.taxNumber")}
            value={MERCHANT_INFO.taxNumber}
          />*/}

          <InformationRow
            label={vm.t("merchant.website")}
            value={MERCHANT_INFO.website}
            href={MERCHANT_INFO.website}
          />

          <InformationRow
            label={vm.t("merchant.phone")}
            value={MERCHANT_INFO.phone}
            href={`tel:${MERCHANT_INFO.phone.replace(/\s/g, "")}`}
          />

          <InformationRow
            label={vm.t("merchant.email")}
            value={MERCHANT_INFO.email}
            href={`mailto:${MERCHANT_INFO.email}`}
          />
        </SectionCard>

        <SectionCard>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 1 }}>
            {vm.t("merchant.customerServiceTitle")}
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {vm.t("merchant.customerServiceDescription")}
          </Typography>

          <InformationRow
            label={vm.t("merchant.address")}
            value={MERCHANT_INFO.customerService.address}
          />

          <InformationRow
            label={vm.t("merchant.phone")}
            value={MERCHANT_INFO.customerService.phone}
            href={`tel:${MERCHANT_INFO.customerService.phone.replace(/\s/g, "")}`}
          />

          <InformationRow
            label={vm.t("merchant.email")}
            value={MERCHANT_INFO.customerService.email}
            href={`mailto:${MERCHANT_INFO.customerService.email}`}
          />
        </SectionCard>

        <SectionCard>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 1 }}>
            {vm.t("merchant.privacyTitle")}
          </Typography>

          <Typography color="text.secondary">
            {vm.t("merchant.privacyDescription")}
          </Typography>
        </SectionCard>
      </Collapse>

      <MenuCard
        title={vm.t("menu.support")}
        description={vm.t("menu.supportDescription")}
        icon={<HelpOutlineOutlinedIcon />}
        open={showSupport}
        onClick={() => vm.setActiveMenuSection(showSupport ? null : "support")}
      />

      <Collapse in={showSupport} unmountOnExit>
        <SectionCard>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 1 }}>
            {vm.t("support.needSupport")}
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {vm.t("support.description")}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              href={`mailto:${MERCHANT_INFO.customerService.email}`}
            >
              {vm.t("support.emailSupport")}
            </Button>

            <Button
              variant="outlined"
              href={`tel:${MERCHANT_INFO.customerService.phone.replace(/\s/g, "")}`}
            >
              {vm.t("support.callSupport")}
            </Button>
          </Stack>
        </SectionCard>
      </Collapse>

      <Divider sx={{ my: 2 }} />

      <Button
        variant="contained"
        color="error"
        startIcon={<LogoutOutlinedIcon />}
        onClick={vm.handleLogout}
        disabled={vm.loading}
      >
        {vm.t("profile.signOut")}
      </Button>
    </Box>
  );
};

export default ProfileScreen;
